import { Check, Undo2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";
import type { ScheduleStagingApi } from "@/domains/pm/components/issues/schedule/scheduleStagingContext";

/**
 * The unsaved-changes bar.
 *
 * It has to carry more weight than a normal toolbar button, because in this
 * view the dependent bars do NOT move until Confirm — the cascade is computed
 * once, server-side. So the count is the only thing telling the user their
 * drag registered and that more will follow, and it stays visible rather than
 * living inside a menu.
 */
export function GanttConfirmBar({ staging }: { staging: ScheduleStagingApi }) {
  const { t } = useLanguage();
  const copy = t.pm.issues.gantt;
  const { staged, saving } = staging;

  if (!staged.isDirty) return null;

  return (
    <div className="flex items-center gap-2 rounded-full border border-[#d97706]/40 bg-[#fef3c7] px-2 py-1">
      <span className="pl-1 text-xs font-medium text-[#92400e]">
        {staged.count === 1
          ? copy.oneUnsavedChange
          : fill(copy.unsavedChanges, { count: String(staged.count) })}
      </span>
      <Button
        variant="ghost"
        size="control"
        disabled={saving}
        onClick={staging.discard}
        className="h-6 gap-1 px-2 text-xs text-[#92400e] hover:bg-[#fde68a]"
      >
        <Undo2 className="h-3.5 w-3.5" />
        {copy.discardChanges}
      </Button>
      <Button
        size="control"
        disabled={saving}
        onClick={staging.confirm}
        className="h-6 gap-1 px-2 text-xs"
      >
        <Check className="h-3.5 w-3.5" />
        {copy.confirmChanges}
      </Button>
    </div>
  );
}

/** A bar or arrow the user has moved but not saved. */
export const GanttStagedBar = {
  /** Dashed amber outline — the same amber the confirm bar uses. */
  className: "outline outline-1 outline-dashed outline-[#d97706] outline-offset-1",
};
